'use client';

import Link from 'next/link';

// Shared "kartica" left-menu style — design_handoff_layout_unifikacija (2026-09-01).
// Every page with a page-specific submenu (Feed, Forum, Trejderi, …) renders it in
// this exact card, so switching sections doesn't feel like switching apps.
export interface PageSidebarItem {
  key: string;
  href?: string;
  /** Render a plain <a> (full page load) for URLs that are not Next.js routes, e.g. the static /semafor page. */
  plain?: boolean;
  emoji: string;
  label: string;
  active?: boolean;
  badge?: string;
  onClick?: () => void;
}

interface PageSidebarProps {
  title: string;
  items: PageSidebarItem[];
  infoTitle?: string;
  infoText?: string;
  children?: React.ReactNode;
}

export default function PageSidebar({ title, items, infoTitle, infoText, children }: PageSidebarProps) {
  return (
    <aside className="hidden md:block shrink-0" style={{ width: 272, padding: '24px 0 24px 24px', position: 'sticky', top: 64, alignSelf: 'flex-start' }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18, padding: '22px 14px 14px' }}>
        <p
          className="uppercase"
          style={{ fontSize: 12, fontWeight: 600, letterSpacing: '1.4px', color: 'var(--text-muted)', padding: '0 14px 10px' }}
        >
          {title}
        </p>
        <nav className="flex flex-col" style={{ gap: 2 }}>
          {items.map((item) => {
            const content = (
              <>
                <span style={{ fontSize: 14, width: 18, textAlign: 'center', flexShrink: 0 }}>{item.emoji}</span>
                <span className="truncate">{item.label}</span>
                {item.badge && (
                  <span
                    className="ml-auto shrink-0"
                    style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', padding: '2px 6px', borderRadius: 999, background: 'var(--accent-muted)', color: 'var(--accent)' }}
                  >
                    {item.badge}
                  </span>
                )}
              </>
            );
            const style: React.CSSProperties = {
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '9px 14px',
              borderRadius: 11,
              fontSize: 14,
              fontWeight: item.active ? 700 : 600,
              background: item.active ? 'var(--accent-muted)' : 'transparent',
              color: item.active ? 'var(--accent)' : 'var(--text-primary)',
              cursor: item.href ? undefined : 'pointer',
            };
            return item.href ? (
              item.plain ? (
                <a key={item.key} href={item.href} style={style}>
                  {content}
                </a>
              ) : (
                <Link key={item.key} href={item.href} style={style}>
                  {content}
                </Link>
              )
            ) : (
              <button key={item.key} onClick={item.onClick} className="w-full text-left" style={style}>
                {content}
              </button>
            );
          })}
        </nav>
        {children}
        {infoTitle && infoText && (
          <div className="mt-[18px]" style={{ background: 'var(--inverse-bg)', borderRadius: 16, padding: '18px 20px' }}>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.1px', color: 'var(--inverse-muted)', marginBottom: 8 }}>
              {infoTitle}
            </p>
            <p style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--inverse-body)' }}>{infoText}</p>
          </div>
        )}
      </div>
    </aside>
  );
}
